import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  consentApi,
  organizationConsentModeApi,
  type ConsentDocumentResponse,
  type TenantConsentMode,
} from '../api/client';
import {
  selectRequiredDocuments,
  buildConsentItems,
  hasUngrantedItems,
  documentLabel,
  type ConsentItem,
} from '../utils/consentItems';
import ConsentChoicesTable from '../components/ConsentChoicesTable';


/**
 * CMN-F01 系: 初回利用前の同意取得フロー（必須同意が残っている場合のみゲートから遷移）
 */
export default function ConsentOnboarding() {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2>(1);
  /**
   * 同意項目は **公開文書を起点** に作る（課題 #77）。
   * 同意履歴から作っていたため、一度も同意していない人には項目が出ず、
   * 同意する手段が画面に無い状態になっていた。
   */
  const [items, setItems] = useState<ConsentItem[]>([]);
  const [documents, setDocuments] = useState<ConsentDocumentResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    setError(null);
    setLoading(true);
    Promise.all([
      consentApi.list(token),
      consentApi.getDocuments(token),
      consentApi.getMasterModes(token),
      organizationConsentModeApi.getSelf(token).catch(() => null),
    ])
      .then(([listRes, docsRes, modesRes, orgRes]) => {
        const rows = listRes.data ?? [];
        const tenantMode: TenantConsentMode | null = orgRes?.consent_mode ?? null;
        const required = selectRequiredDocuments(modesRes, tenantMode);
        const built = buildConsentItems(required, rows);
        setItems(built);
        setDocuments(Array.isArray(docsRes) ? docsRes : []);
        // 同意すべき文書が 1 件もない、または全て同意済みなら先へ進める
        if (required.length === 0 || !hasUngrantedItems(built)) {
          navigate('/dashboard', { replace: true });
        }
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : t('dashboard.errorLoad'));
        setItems([]);
        setDocuments([]);
      })
      .finally(() => setLoading(false));
  }, [getToken, navigate, t]);

  useEffect(() => {
    load();
  }, [load]);

  // 同意すべき項目が残っている間は先へ進ませない
  const canFinish = items.length === 0 || !hasUngrantedItems(items);

  const handleFinish = () => {
    navigate('/dashboard', { replace: true });
  };

  if (loading) {
    return (
      <div className="page consent-onboarding-page">
        <div className="dashboard-loading">{t('common.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page consent-onboarding-page">
        <h1>{t('onboarding.consent.title')}</h1>
        <div className="settings-error" role="alert">
          <p>{error}</p>
          <button type="button" className="dashboard-error-retry" onClick={load}>
            {t('dashboard.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page consent-onboarding-page">
      <h1>{t('onboarding.consent.title')}</h1>

      {step === 1 && (
        <section className="consent-onboarding-step" aria-labelledby="onboarding-step1-heading">
          <h2 id="onboarding-step1-heading">{t('onboarding.consent.step1Title')}</h2>
          <p className="settings-intro">{t('onboarding.consent.step1Body')}</p>
          <button type="button" className="admin-btn-primary" onClick={() => setStep(2)}>
            {t('onboarding.consent.step1Next')}
          </button>
        </section>
      )}

      {step === 2 && (
        <section className="consent-onboarding-step" aria-labelledby="onboarding-step2-heading">
          <h2 id="onboarding-step2-heading">{t('onboarding.consent.step2Title')}</h2>
          <p className="settings-intro">{t('onboarding.consent.step2Intro')}</p>

          <div className="settings-card">
            <h3>{t('onboarding.consent.step2Choices')}</h3>
            {items.length === 0 ? (
              <p className="settings-empty">{t('onboarding.consent.noRows')}</p>
            ) : (
              /* 本文を開いてから同意する（課題 #77）。本文は各行の「本文を読む」で開く */
              <ConsentChoicesTable
                items={items}
                documents={documents}
                label={(item) => documentLabel(item.document)}
                onChanged={load}
              />
            )}
          </div>

          <div className="consent-onboarding-actions">
            <button type="button" className="admin-btn-secondary" onClick={() => setStep(1)}>
              {t('onboarding.consent.back')}
            </button>
            <button
              type="button"
              className="admin-btn-primary"
              onClick={handleFinish}
              disabled={!canFinish}
            >
              {t('onboarding.consent.finish')}
            </button>
          </div>
          {!canFinish && items.length > 0 && (
            <p className="consent-onboarding-hint" role="status">
              {t('onboarding.consent.finishHint')}
            </p>
          )}
        </section>
      )}
    </div>
  );
}
